import { normalize } from 'normalizr';
import moment from 'moment';
import { cellLengthInMinutes as cellLength } from '../../../settings/scheduler';
import { postingSchema, assignmentSchema } from '../../../utils/normalizr/postingSchema';
import {
  POSTINGS_GET_REQUEST,
  POSTINGS_GET_SUCCESS,
  POSTINGS_GET_FAILURE,
  LOGIN_SUCCESS,
  TRAVEL_TIME_FOR_TREATMENT_GET_SUCCESS,
  TRAVEL_TIME_FOR_TREATMENT_GET_REQUEST,
  UPDATE_POSTINGS_SUCCESS,
  UPDATE_POSTINGS_AFTER_ASSIGN_SUCCESS,
  UPDATE_POSTINGS_AFTER_RESCHEDULE_SUCCESS,
  UPDATE_POSTINGS_AFTER_UNASSIGN_SUCCESS,
  TEAM_AVAILABILITY_GET_SUCCESS,
  POSTINGS_RESET_DATA,
} from '../../../actions/constants/types';
import { filterResponseBySchedulerModeView } from '../../../actions/entities/postings';

const initialPostingsState = {
  isFetching: false,
  serverDateTime: '',
  availableTime: {},
  assignedTime: {},
  assignedTimeForAssignments: {},
  assignedTimeForNonPatientAssignments: {},
  travelTimes: {},
  travelTimesPostcode: null,
};

function postings(state = initialPostingsState, action) {
  switch (action.type) {
    case POSTINGS_GET_REQUEST:
      return { ...state, isFetching: !action.silent };
    case TRAVEL_TIME_FOR_TREATMENT_GET_REQUEST:
      return { ...state, travelTimes: {}, travelTimesPostcode: null };
    case TRAVEL_TIME_FOR_TREATMENT_GET_SUCCESS:
      return {
        ...state,
        travelTimes: action.response.data.postcodeTravelTimes.travelTimes,
        travelTimesPostcode: action.response.data.postcodeTravelTimes.postcode,
      };
    case POSTINGS_GET_SUCCESS: {
      const filteredResponse = filterResponseBySchedulerModeView(
        action.response,
        action.isNightMode,
        action.selectedDate,
        action.schedulerModeStartTime,
        action.schedulerModeEndTime,
      );
      const normalizeResult = normalize(filteredResponse.data.postings, [postingSchema]);
      const clinicianBasePostcodes = action.response.data.postings
        .filter(posting => posting.clinician.hasValidBasePostCode)
        .map(posting => posting.clinician.basePostCode);
      return {
        ...state,
        isFetching: false,
        dayStart: action.response.data.firstAvailabilityTime,
        dayEnd: action.response.data.lastAvailabilityTime,
        availableTime: action.response.data.availableTime,
        assignedTime: action.response.data.assignedTime,
        assignedTimeForAssignments: action.response.data.assignedTimeForAssignments,
        assignedTimeForNonPatientAssignments: action.response.data.assignedTimeForNonPatientAssignments,
        lastUpdated: action.receivedAt,
        serverDateTime: action.response.data.serverDateTime,
        entities: normalizeResult.entities,
        postingsOrder: normalizeResult.result,
        clinicianBasePostcodes,
      };
    }
    case POSTINGS_RESET_DATA: {
      return {
        ...state,
        serverDateTime: '',
        availableTime: {},
        assignedTime: {},
        assignedTimeForAssignments: {},
        assignedTimeForNonPatientAssignments: {},
        travelTimes: {},
        travelTimesPostcode: null,
        entities: {},
        postingsOrder: {},
      };
    }
    case POSTINGS_GET_FAILURE:
    case LOGIN_SUCCESS:
      return initialPostingsState;
    case UPDATE_POSTINGS_SUCCESS: {
      const res = {
        ...state.entities,
        assignments: {
          ...state.entities.assignments,
          ...normalize(action.response.data.availabilities.reduce((r, k) => r.concat(k.assignments), []), [
            assignmentSchema,
          ]).entities.assignments,
        },
      };

      const availabilities = { ...state.entities.availabilities };
      action.response.data.availabilities.forEach(availability => {
        const result = normalize(availability.assignments, [assignmentSchema]).entities.assignments;
        availabilities[availability.id].assignments = Object.keys(result).map(x => parseInt(x, 10));
      });
      Object.keys(availabilities).forEach(id => {
        availabilities[id].dropCellTargets = availabilities[id].dropCellTargets.map(e =>
          e.map(inner =>
            inner === action.response.data.oldAssignGuid && action.response.data.newAssignGuid !== 0
              ? action.response.data.newAssignGuid
              : inner,
          ),
        );
      });
      return { ...state, entities: { ...res, assignments: res.assignments } };
    }
    case UPDATE_POSTINGS_AFTER_ASSIGN_SUCCESS: {
      const res = {
        ...state.entities,
        assignments: {
          ...state.entities.assignments,
          ...normalize(action.response.data.availabilities.reduce((r, k) => r.concat(k.assignments), []), [
            assignmentSchema,
          ]).entities.assignments,
        },
      };

      const availabilities = { ...state.entities.availabilities };
      action.response.data.availabilities.forEach(availability => {
        const result = normalize(availability.assignments, [assignmentSchema]).entities.assignments;
        if (availabilities[availability.id]) {
          availabilities[availability.id].assignments = Object.keys(result).map(x => parseInt(x, 10));
        }
      });
      Object.keys(availabilities).forEach(id => {
        availabilities[id].dropCellTargets = availabilities[id].dropCellTargets.map(e =>
          e.map(inner =>
            inner === action.response.data.oldAssignGuid && action.response.data.newAssignGuid !== 0
              ? action.response.data.newAssignGuid
              : inner,
          ),
        );
        const newGuids = action.response.data.treatmentInstancesCopiesGuids;
        if (newGuids && newGuids.length) {
          const assignmentStartDate = moment(action.response.data.startDate);
          const availabilityStartDate = moment(availabilities[id].startDate);
          const availabilityEndDate = moment(availabilities[id].endDate);

          const isBeforeAvailabilityEnd =
            moment.duration(availabilityEndDate.diff(assignmentStartDate)).asMinutes() > 0;
          const index = moment.duration(assignmentStartDate.diff(availabilityStartDate)).asMinutes() / cellLength;

          if (index >= 0 && isBeforeAvailabilityEnd) {
            availabilities[id].dropCellTargets[index] = availabilities[id].dropCellTargets[index].concat(newGuids);
          }
        }
      });
      return { ...state, entities: { ...res, assignments: res.assignments, availabilities } };
    }
    case UPDATE_POSTINGS_AFTER_RESCHEDULE_SUCCESS: {
      const res = {
        ...state.entities,
        assignments: {
          ...state.entities.assignments,
          ...normalize(action.response.data.availabilities.reduce((r, k) => r.concat(k.assignments), []), [
            assignmentSchema,
          ]).entities.assignments,
        },
      };

      const availabilities = { ...state.entities.availabilities };
      action.response.data.availabilities.forEach(availability => {
        const currentAvailability = availabilities[availability.id];
        if (currentAvailability) {
          const result = normalize(availability.assignments, [assignmentSchema]).entities.assignments;
          if (result) {
            availabilities[availability.id].assignments = Object.keys(result).map(x => parseInt(x, 10));
          } else {
            availabilities[availability.id].assignments = Object.assign([]);
          }
        }
      });
      Object.keys(availabilities).forEach(id => {
        availabilities[id].dropCellTargets = availabilities[id].dropCellTargets.map(e =>
          e.map(inner =>
            inner === action.response.data.oldAssignGuid && action.response.data.newAssignGuid !== 0
              ? action.response.data.newAssignGuid
              : inner,
          ),
        );

        const newGuids = action.response.data.treatmentInstancesCopiesGuids;
        if (newGuids && newGuids.length) {
          const assignmentStartDate = moment(action.response.data.startDate);
          const oldAssignmentStartDate = moment(action.response.data.oldDate);
          const availabilityStartDate = moment(availabilities[id].startDate);
          const availabilityEndDate = moment(availabilities[id].endDate);
          const isBeforeAvailabilityEnd =
            moment.duration(availabilityEndDate.diff(assignmentStartDate)).asMinutes() > 0;

          const isBeforeAvailabilityOldEnd =
            moment.duration(availabilityEndDate.diff(oldAssignmentStartDate)).asMinutes() > 0;

          const assignmentOldStartDate = moment(action.response.data.oldDate);
          const indexRemove =
            moment.duration(assignmentOldStartDate.diff(availabilityStartDate)).asMinutes() / cellLength;
          if (indexRemove >= 0 && isBeforeAvailabilityOldEnd) {
            availabilities[id].dropCellTargets[indexRemove] = availabilities[id].dropCellTargets[indexRemove].filter(
              x => !newGuids.find(y => y === x),
            );
          }

          const index = moment.duration(assignmentStartDate.diff(availabilityStartDate)).asMinutes() / cellLength;
          if (index >= 0 && isBeforeAvailabilityEnd) {
            availabilities[id].dropCellTargets[index] = availabilities[id].dropCellTargets[index].concat(newGuids);
          }
        }
      });
      return { ...state, entities: { ...res, assignments: res.assignments, availabilities } };
    }
    case UPDATE_POSTINGS_AFTER_UNASSIGN_SUCCESS: {
      const res = {
        ...state.entities,
        assignments: {
          ...state.entities.assignments,
          ...normalize(action.response.data.availabilities.reduce((r, k) => r.concat(k.assignments), []), [
            assignmentSchema,
          ]).entities.assignments,
        },
      };

      const availabilities = { ...state.entities.availabilities };
      action.response.data.availabilities.forEach(availability => {
        const currentAvailability = availabilities[availability.id];
        if (currentAvailability) {
          const result = normalize(availability.assignments, [assignmentSchema]).entities.assignments;
          if (result) {
            currentAvailability.assignments = Object.keys(result).map(x => parseInt(x, 10));
          } else {
            currentAvailability.assignments = Object.assign([]);
          }
        }
      });
      Object.keys(availabilities).forEach(id => {
        availabilities[id].dropCellTargets = availabilities[id].dropCellTargets.map(e =>
          e.map(inner =>
            inner === action.response.data.oldAssignGuid && action.response.data.newAssignGuid !== 0
              ? action.response.data.newAssignGuid
              : inner,
          ),
        );
        const newGuids = action.response.data.treatmentInstancesCopiesGuids;
        if (newGuids && newGuids.length) {
          const oldAssignmentStartDate = moment(action.response.data.oldDate);
          const availabilityStartDate = moment(availabilities[id].startDate);
          const availabilityEndDate = moment(availabilities[id].endDate);
          const isBeforeAvailabilityOldEnd =
            moment.duration(availabilityEndDate.diff(oldAssignmentStartDate)).asMinutes() > 0;

          const assignmentOldStartDate = moment(action.response.data.oldDate);
          const indexRemove =
            moment.duration(assignmentOldStartDate.diff(availabilityStartDate)).asMinutes() / cellLength;
          if (indexRemove >= 0 && isBeforeAvailabilityOldEnd) {
            availabilities[id].dropCellTargets[indexRemove] = availabilities[id].dropCellTargets[indexRemove].filter(
              x => !newGuids.find(y => y === x),
            );
          }
        }
      });
      return { ...state, entities: { ...res, assignments: res.assignments, availabilities } };
    }
    case TEAM_AVAILABILITY_GET_SUCCESS: {
      return {
        ...state,
        availableTime: action.response.data.availableTime,
        assignedTime: action.response.data.assignedTime,
        assignedTimeForAssignments: action.response.data.assignedTimeForAssignments,
        assignedTimeForNonPatientAssignments: action.response.data.assignedTimeForNonPatientAssignments,
      };
    }
    default:
      return state;
  }
}

export default postings;
